import React, { useState, useEffect, useCallback } from 'react';
import { officeExpenseAPI, uploadAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { DatePicker } from '../../components/ui/date-picker';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Receipt,
  IndianRupee,
  FileImage,
  Upload,
  X,
  Settings,
  Filter,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const OfficeExpenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isCatOpen, setIsCatOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [newCatName, setNewCatName] = useState('');

  const [filterMonth, setFilterMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [filterCategory, setFilterCategory] = useState('all');

  const emptyForm = { date: '', amount: '', category: '', description: '', payee_vendor: '', receipt_url: '' };
  const [form, setForm] = useState(emptyForm);
  const [uploading, setUploading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterMonth) params.month = filterMonth;
      if (filterCategory && filterCategory !== 'all') params.category = filterCategory;
      const [expRes, catRes] = await Promise.all([
        officeExpenseAPI.getAll(params),
        officeExpenseAPI.getCategories(),
      ]);
      setExpenses(expRes.data.expenses);
      setTotal(expRes.data.total);
      setCategories(catRes.data);
    } catch (err) {
      console.error('Failed to load expenses', err);
    } finally {
      setLoading(false);
    }
  }, [filterMonth, filterCategory]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh every 30 seconds — but NOT while a dialog is open
  useEffect(() => {
    if (isAddOpen || isEditOpen || isCatOpen) return;
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData, isAddOpen, isEditOpen, isCatOpen]);

  const handleReceiptUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadAPI.uploadDocument(file);
      setForm(prev => ({ ...prev, receipt_url: res.data.url }));
    } catch (err) {
      alert('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!form.date || !form.amount || !form.category || !form.description) {
      alert('Please fill all required fields');
      return;
    }
    setSaving(true);
    try {
      await officeExpenseAPI.create({ ...form, amount: parseFloat(form.amount) });
      setForm(emptyForm);
      setIsAddOpen(false);
      await fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!form.date || !form.amount || !form.category || !form.description) {
      alert('Please fill all required fields');
      return;
    }
    setSaving(true);
    try {
      await officeExpenseAPI.update(editingExpense.id, { ...form, amount: parseFloat(form.amount) });
      setIsEditOpen(false);
      setEditingExpense(null);
      setForm(emptyForm);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this expense? It will be soft-deleted and kept in audit log.')) return;
    try {
      await officeExpenseAPI.delete(id);
      fetchData();
    } catch (err) {
      alert('Failed to delete');
    }
  };

  const openEdit = (exp) => {
    setEditingExpense(exp);
    setForm({
      date: exp.date, amount: String(exp.amount), category: exp.category,
      description: exp.description, payee_vendor: exp.payee_vendor || '',
      receipt_url: exp.receipt_url || '',
    });
    setIsEditOpen(true);
  };

  const handleAddCategory = async () => {
    if (!newCatName.trim()) return;
    try {
      await officeExpenseAPI.createCategory({ name: newCatName.trim() });
      setNewCatName('');
      const catRes = await officeExpenseAPI.getCategories();
      setCategories(catRes.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to add category');
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Remove this category?')) return;
    try {
      await officeExpenseAPI.deleteCategory(id);
      const catRes = await officeExpenseAPI.getCategories();
      setCategories(catRes.data);
    } catch (err) {
      alert('Failed to delete');
    }
  };

  const getReceiptUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    if (url.startsWith('/uploads/')) return `${BACKEND_URL}/api${url}`;
    return `${BACKEND_URL}${url}`;
  };

  const handleViewReceipt = async (url) => {
    const receiptUrl = getReceiptUrl(url);
    if (!receiptUrl) return;
    // For images, fetch via auth and create blob URL
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(receiptUrl, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!resp.ok) throw new Error('Failed to fetch');
      const blob = await resp.blob();
      setPreviewUrl(URL.createObjectURL(blob));
    } catch {
      setPreviewUrl(receiptUrl);
    }
  };

  const formatMonth = (m) => {
    const [y, mo] = m.split('-');
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${months[parseInt(mo) - 1]} ${y}`;
  };

  const ExpenseFormFields = (
    <div className="space-y-4 mt-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Date *</Label>
          <DatePicker value={form.date} onChange={v => setForm(p => ({ ...p, date: v }))} placeholder="Pick date" data-testid="expense-date" />
        </div>
        <div className="space-y-2">
          <Label>Amount (Rs.) *</Label>
          <Input type="number" min="0" step="0.01" placeholder="0.00" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))} data-testid="expense-amount" />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Category *</Label>
        <Select value={form.category} onValueChange={v => setForm(p => ({ ...p, category: v }))}>
          <SelectTrigger data-testid="expense-category">
            <SelectValue placeholder="Select category" />
          </SelectTrigger>
          <SelectContent>
            {categories.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Description *</Label>
        <Textarea placeholder="What was this expense for?" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} data-testid="expense-description" />
      </div>
      <div className="space-y-2">
        <Label>Payee / Vendor</Label>
        <Input placeholder="e.g. Swiggy, Uber, Amazon" value={form.payee_vendor} onChange={e => setForm(p => ({ ...p, payee_vendor: e.target.value }))} data-testid="expense-vendor" />
      </div>
      <div className="space-y-2">
        <Label>Receipt (optional)</Label>
        <div className="flex items-center gap-3">
          {form.receipt_url ? (
            <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
              <FileImage className="w-4 h-4" />
              Receipt attached
              <button onClick={() => setForm(p => ({ ...p, receipt_url: '' }))} className="ml-1 hover:text-red-500"><X className="w-3 h-3" /></button>
            </div>
          ) : (
            <label className="flex items-center gap-2 px-4 py-2 border border-dashed border-slate-300 rounded-lg text-sm text-slate-500 cursor-pointer hover:border-blue-400 hover:text-blue-500 transition-colors">
              <Upload className="w-4 h-4" />
              {uploading ? 'Uploading...' : 'Upload receipt'}
              <input type="file" className="hidden" accept="image/*,.pdf" onChange={handleReceiptUpload} disabled={uploading} />
            </label>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="office-expenses-page">
      {/* Header with total */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-[#1D35A0]">Office Expenses</h2>
          <p className="text-sm text-slate-500">Company-level costs managed by Admin</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => setIsCatOpen(true)} data-testid="manage-categories-btn">
            <Settings className="w-4 h-4 mr-1" /> Categories
          </Button>
          <Button onClick={() => { setForm(emptyForm); setIsAddOpen(true); }} className="bg-[#1D35A0] hover:bg-[#152A80] text-white" data-testid="add-expense-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Expense
          </Button>
        </div>
      </div>

      {/* Monthly total + Filters */}
      <div className="bg-white rounded-lg border border-slate-200 p-3.5 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
              <IndianRupee className="w-4 h-4 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total for {formatMonth(filterMonth)}</p>
              <p className="text-xl font-bold text-[#1D35A0]" data-testid="monthly-total">Rs. {total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Input type="month" value={filterMonth} onChange={e => setFilterMonth(e.target.value)} className="w-44" data-testid="filter-month" />
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-48" data-testid="filter-category">
                <Filter className="w-4 h-4 mr-1 text-slate-400" />
                <SelectValue placeholder="All categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Expenses Table */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1D35A0]" />
          </div>
        ) : expenses.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-slate-400">
            <Receipt className="w-10 h-10 mb-2" />
            <p>No expenses found for this period</p>
          </div>
        ) : (
          <div className="space-y-3">
            {expenses.map(exp => (
              <div key={exp.id} className="bg-white rounded-lg border border-slate-200 shadow-sm p-4" data-testid={`expense-row-${exp.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-semibold text-[#1D35A0]">{exp.description}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">{exp.category}</span>
                      <span className="text-xs text-slate-400">{exp.date}</span>
                    </div>
                  </div>
                  <p className="text-lg font-bold text-[#1D35A0]">Rs. {exp.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <div className="flex items-center gap-3 text-xs text-slate-500">
                    {exp.payee_vendor && <span>Vendor: <span className="font-medium text-slate-600">{exp.payee_vendor}</span></span>}
                    {exp.receipt_url && (
                      <button onClick={() => handleViewReceipt(exp.receipt_url)} className="flex items-center gap-1 text-blue-500 hover:text-blue-700" data-testid={`receipt-btn-${exp.id}`}>
                        <FileImage className="w-3.5 h-3.5" /> Receipt
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => openEdit(exp)} className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-blue-600" data-testid={`edit-expense-${exp.id}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(exp.id)} className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-red-600" data-testid={`delete-expense-${exp.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Dialog */}
      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent className="max-w-lg" data-testid="add-expense-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1D35A0]">Add Office Expense</DialogTitle>
            <DialogDescription>Record a new company expense</DialogDescription>
          </DialogHeader>
          {ExpenseFormFields}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => { setIsAddOpen(false); setForm(emptyForm); }}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#1D35A0] hover:bg-[#152A80] text-white" data-testid="save-expense-btn">
              {saving ? 'Saving...' : 'Add Expense'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={v => { setIsEditOpen(v); if (!v) { setEditingExpense(null); setForm(emptyForm); } }}>
        <DialogContent className="max-w-lg" data-testid="edit-expense-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1D35A0]">Edit Expense</DialogTitle>
            <DialogDescription>Update expense details</DialogDescription>
          </DialogHeader>
          {ExpenseFormFields}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={() => { setIsEditOpen(false); setEditingExpense(null); setForm(emptyForm); }}>Cancel</Button>
            <Button onClick={handleUpdate} disabled={saving} className="bg-[#1D35A0] hover:bg-[#152A80] text-white" data-testid="save-expense-btn">
              {saving ? 'Saving...' : 'Update Expense'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Receipt Preview Dialog */}
      <Dialog open={!!previewUrl} onOpenChange={() => setPreviewUrl(null)}>
        <DialogContent className="max-w-2xl" data-testid="receipt-preview-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1D35A0]">Receipt Preview</DialogTitle>
            <DialogDescription>Uploaded receipt</DialogDescription>
          </DialogHeader>
          {previewUrl && (
            previewUrl.endsWith('.pdf') ? (
              <iframe src={previewUrl} className="w-full h-[500px] rounded-lg border" title="Receipt PDF" />
            ) : (
              <img src={previewUrl} alt="Receipt" className="w-full max-h-[500px] object-contain rounded-lg" />
            )
          )}
        </DialogContent>
      </Dialog>

      {/* Categories Management Dialog */}
      <Dialog open={isCatOpen} onOpenChange={setIsCatOpen}>
        <DialogContent className="max-w-md" data-testid="categories-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1D35A0]">Manage Categories</DialogTitle>
            <DialogDescription>Add or remove expense categories</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="flex gap-2">
              <Input placeholder="New category name" value={newCatName} onChange={e => setNewCatName(e.target.value)} data-testid="new-category-input" />
              <Button onClick={handleAddCategory} className="bg-[#1D35A0] hover:bg-[#152A80] text-white" data-testid="add-category-btn">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {categories.map(c => (
                <div key={c.id} className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg">
                  <span className="text-sm font-medium text-slate-700">{c.name}</span>
                  <button onClick={() => handleDeleteCategory(c.id)} className="text-slate-400 hover:text-red-500" data-testid={`delete-cat-${c.id}`}>
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OfficeExpenses;
